import { RegularMetricId } from '../../../../api/types-graphql';
import { StoryboardComponentDependencies } from '../../../../common/components/dashboards/types';
import {
  BenchmarkPeriods,
  KpiFactboxBenchmarkPeriods,
  KpiFactboxBenchmarkTypes,
} from '../../../../common/components/data-viz-containers/common/types';
import { KpiFactboxSingleMetricVizViews } from '../../../../common/components/data-viz-containers/kpi-factbox-single-metric/types';
import { OverTimeUnsegmentedVizViews } from '../../../../common/components/data-viz-containers/over-time-unsegmented/types';
import { TimePeriodUnsegmentedVizViews } from '../../../../common/components/data-viz-containers/time-period-unsegmented/types';
import {
  StoryboardComponentIds,
  StoryboardItemConfig,
} from '../../../../common/components/storyboard-components/common/types';
import { Languages } from '../../../../constants';
import {
  DataTypes,
  EmployeeDataFields,
  TimeSelectionType,
} from '../../../../types';

const yoyDeltaBenchmark = [
  {
    benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
    benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
  },
];

const workforceCompositionSubtitle: Record<Languages, string> = {
  [Languages.EN]: 'As of the latest data refresh month',
  [Languages.JA]: '※最新のデータ更新月時点',
};

const createWorkforceCompositionBarChart = (
  chartId: string,
  title: Record<Languages, string>,
  groupByField: EmployeeDataFields,
  storyboardDateUtils: StoryboardComponentDependencies['storyboardDateUtils']
): StoryboardItemConfig => {
  const { nowEndOfMonth } = storyboardDateUtils;
  return {
    chartId,
    layoutConfig: { gridColSpan: 1, gridRowSpan: 1 },
    componentConfig: {
      componentId: StoryboardComponentIds.TIME_PERIOD_UNSEGMENTED,
      dataConfig: {
        title,
        subtitle: workforceCompositionSubtitle,
        queries: [
          {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0001_1IndividualsCount,
            },
          },
        ],
        timeSelection: {
          type: TimeSelectionType.CalendarYearSingleValueByMonths,
          input: { start: nowEndOfMonth, end: nowEndOfMonth },
        },
        groupBy: {
          type: 'non-hierarchical',
          dataField: {
            dataType: DataTypes.EMPLOYEE,
            dataField: groupByField,
          },
        },
      },
      selectedView: TimePeriodUnsegmentedVizViews.BAR_CHART,
    },
  };
};

export const getIPexHumanCapitalPage1Components = ({
  storyboardDateUtils,
  domainPreferences,
}: StoryboardComponentDependencies): StoryboardItemConfig[] => {
  const { nowEndOfMonth, startOfFiscalYear } = storyboardDateUtils;

  return [
    {
      chartId: 'ipex-hc-domestic-headcount',
      layoutConfig: { gridColSpan: 1, gridRowSpan: 1 },
      componentConfig: {
        componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
        dataConfig: {
          title: {
            [Languages.EN]: 'Domestic Headcount',
            [Languages.JA]: '国内従業員数',
          },
          footer: {
            [Languages.EN]: 'As of the latest data refresh month',
            [Languages.JA]: '※最新のデータ更新月時点',
          },
          section: {
            query: {
              metric: {
                type: 'RegularMetricId',
                value: RegularMetricId.Pm0001_1IndividualsCount,
              },
              timeSelection: {
                type: TimeSelectionType.CalendarYearSingleValueByMonths,
                input: { start: nowEndOfMonth, end: nowEndOfMonth },
              },
            },
            benchmarkConfigs: yoyDeltaBenchmark,
          },
        },
        selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
      },
    },
    {
      chartId: 'ipex-hc-management-ratio',
      layoutConfig: { gridColSpan: 1, gridRowSpan: 1 },
      componentConfig: {
        componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
        dataConfig: {
          title: {
            [Languages.EN]: 'Management Ratio',
            [Languages.JA]: '管理職比率',
          },
          footer: {
            [Languages.EN]: 'Ratio as of the latest data refresh month',
            [Languages.JA]: '※最新のデータ更新月時点の比率',
          },
          section: {
            query: {
              metric: {
                type: 'RegularMetricId',
                value: RegularMetricId.Pm0014_2MngDefPercHc,
              },
              timeSelection: {
                type: TimeSelectionType.CalendarYearSingleValueByMonths,
                input: { start: nowEndOfMonth, end: nowEndOfMonth },
              },
            },
            benchmarkConfigs: yoyDeltaBenchmark,
          },
        },
        selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
      },
    },
    {
      chartId: 'ipex-hc-female-management-ratio',
      layoutConfig: { gridColSpan: 1, gridRowSpan: 1 },
      componentConfig: {
        componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
        dataConfig: {
          title: {
            [Languages.EN]: 'Female Management Ratio',
            [Languages.JA]: '女性管理職比率',
          },
          footer: {
            [Languages.EN]: 'Ratio as of the latest data refresh month',
            [Languages.JA]: '※最新のデータ更新月時点の比率',
          },
          section: {
            query: {
              metric: {
                type: 'RegularMetricId',
                value: RegularMetricId.Pm0016_3FMngDefPercMng,
              },
              timeSelection: {
                type: TimeSelectionType.CalendarYearSingleValueByMonths,
                input: { start: nowEndOfMonth, end: nowEndOfMonth },
              },
            },
            benchmarkConfigs: yoyDeltaBenchmark,
          },
        },
        selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
      },
    },
    {
      chartId: 'ipex-hc-gender-balance-pie',
      layoutConfig: { gridColSpan: 1, gridRowSpan: 1 },
      componentConfig: {
        componentId: StoryboardComponentIds.TIME_PERIOD_UNSEGMENTED,
        dataConfig: {
          title: {
            [Languages.EN]: 'Gender Balance',
            [Languages.JA]: '男女比',
          },
          footer: {
            [Languages.EN]: 'Ratio as of the latest data refresh month',
            [Languages.JA]: '※最新のデータ更新月時点の比率',
          },
          benchmarkConfigs: yoyDeltaBenchmark,
          queries: [
            {
              metric: {
                type: 'RegularMetricId',
                value: RegularMetricId.Pm0001_1IndividualsCount,
              },
            },
          ],
          timeSelection: {
            type: TimeSelectionType.CalendarYearSingleValueByMonths,
            input: { start: nowEndOfMonth, end: nowEndOfMonth },
          },
          groupBy: {
            type: 'non-hierarchical',
            dataField: {
              dataType: DataTypes.EMPLOYEE,
              dataField: EmployeeDataFields.GENDER,
            },
          },
          chartColors: ['#7245b2', '#25b1e4'],
        },
        selectedView: TimePeriodUnsegmentedVizViews.PIE_CHART,
      },
    },
    {
      chartId: 'ipex-hc-attrition-rate',
      layoutConfig: { gridColSpan: 1, gridRowSpan: 1 },
      componentConfig: {
        componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
        dataConfig: {
          title: {
            [Languages.EN]: 'Attrition Rate',
            [Languages.JA]: '離職率',
          },
          footer: {
            [Languages.EN]: 'Rate as of the latest data refresh month',
            [Languages.JA]: '※最新のデータ更新月時点の比率',
          },
          section: {
            query: {
              metric: {
                type: 'RegularMetricId',
                value: RegularMetricId.Pm0076_2AttritionMonthly,
              },
              timeSelection: {
                type: TimeSelectionType.CalendarYearSingleValueByMonths,
                input: { start: nowEndOfMonth, end: nowEndOfMonth },
              },
            },
            benchmarkConfigs: yoyDeltaBenchmark,
          },
        },
        selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
      },
    },
    {
      chartId: 'ipex-hc-leavers-fy',
      layoutConfig: { gridColSpan: 1, gridRowSpan: 1 },
      componentConfig: {
        componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
        dataConfig: {
          title: {
            [Languages.EN]: 'Leavers',
            [Languages.JA]: '退職者数',
          },
          footer: {
            [Languages.EN]:
              'Cumulative total from the start of the fiscal year',
            [Languages.JA]: '※期初(年始)からの累計',
          },
          section: {
            query: {
              metric: {
                type: 'RegularMetricId',
                value: RegularMetricId.Pm0007_1LeaversTermDateLeavers,
              },
              timeSelection: {
                type: TimeSelectionType.CalendarYearSingleValueByMonths,
                input: {
                  start: startOfFiscalYear(domainPreferences),
                  end: nowEndOfMonth,
                },
              },
            },
            benchmarkConfigs: [
              {
                benchmarkPeriod: KpiFactboxBenchmarkPeriods.PRIOR_PERIOD,
                benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
              },
            ],
          },
        },
        selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
      },
    },
  ];
};

export const getIPexHumanCapitalPage2Components = ({
  storyboardDateUtils,
}: StoryboardComponentDependencies): StoryboardItemConfig[] => {
  const { twelveMonthsAgoFromNowEndOfMonth, nowEndOfMonth } =
    storyboardDateUtils;

  return [
    createWorkforceCompositionBarChart(
      'ipex-hc-tenure',
      {
        [Languages.EN]: 'Tenure',
        [Languages.JA]: '勤続年数',
      },
      EmployeeDataFields.TENURE_GROUP,
      storyboardDateUtils
    ),
    createWorkforceCompositionBarChart(
      'ipex-hc-job-grade',
      {
        [Languages.EN]: 'Job Grade',
        [Languages.JA]: '職務等級',
      },
      EmployeeDataFields.JOB_GRADE_LEVEL_1,
      storyboardDateUtils
    ),
    createWorkforceCompositionBarChart(
      'ipex-hc-age-group',
      {
        [Languages.EN]: 'Age Group',
        [Languages.JA]: '年代',
      },
      EmployeeDataFields.AGE_GROUP,
      storyboardDateUtils
    ),
    createWorkforceCompositionBarChart(
      'ipex-hc-employment-type',
      {
        [Languages.EN]: 'Employee Type',
        [Languages.JA]: '雇用別人員構成',
      },
      EmployeeDataFields.EMPLOYMENT_TYPE_LEVEL_1,
      storyboardDateUtils
    ),
    {
      chartId: 'ipex-hc-joiners-leavers-trend',
      layoutConfig: { gridColSpan: 2, gridRowSpan: 1 },
      componentConfig: {
        componentId: StoryboardComponentIds.OVERTIME_UNSEGMENTED,
        dataConfig: {
          title: {
            [Languages.EN]: 'Trends in Joiners & Leavers',
            [Languages.JA]: '入社者数・離職者数の推移',
          },
          subtitle: {
            [Languages.EN]: 'Last 12 months from the latest data refresh month',
            [Languages.JA]: '※最新のデータ更新月から直近12ヶ月',
          },
          queries: [
            {
              metric: {
                type: 'RegularMetricId',
                value: RegularMetricId.Pm0006_1JoinersJoiners,
              },
            },
            {
              metric: {
                type: 'RegularMetricId',
                value: RegularMetricId.Pm0007_1LeaversTermDateLeavers,
              },
            },
          ],
          timeSelection: {
            type: TimeSelectionType.CalendarYearMonthly,
            input: {
              start: twelveMonthsAgoFromNowEndOfMonth,
              end: nowEndOfMonth,
            },
          },
          benchmarkConfig: {
            benchmarkPeriod: BenchmarkPeriods.YEAR_ON_YEAR,
          },
        },
        selectedView: OverTimeUnsegmentedVizViews.LINE_CHART,
      },
    },
  ];
};
